import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import CustomFieldService from './customFieldAdmin.service';

type ActionType = 'upsertCustomField' | 'archiveCustomField' | 'restoreCustomField' | 'updateTypes';

interface MutationAction {
  actionType: ActionType;
  payload: any;
}

const useCustomFieldMutation = (): {
  executeCustomFieldAction: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  isUpdating: Record<string, boolean>;
  setIsUpdating: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  executeCustomFieldActionMutation: UseMutationResult<any, unknown, MutationAction, unknown>;

} => {
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const params = useParams({ from: '/_auth/custom-fields/$customFieldId' });

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const customFieldsService = CustomFieldService();

  const actionMap: { [key in ActionType]: Function } = {
    upsertCustomField: customFieldsService.postCustomField,
    archiveCustomField: customFieldsService.archiveRestoreCustomField,
    restoreCustomField: customFieldsService.archiveRestoreCustomField,
    updateTypes: customFieldsService.postCustomFieldTypes,
  };

  const successMessages: { [key in ActionType]: string } = {
    upsertCustomField: 'Custom field successfully saved',
    archiveCustomField: 'Custom field successfully archived',
    restoreCustomField: 'Custom field successfully restored',
    updateTypes: 'Types successfully updated',
  };

  const failureMessages: { [key in ActionType]: string } = {
    upsertCustomField: 'Failed to save Custom field',
    archiveCustomField: 'Failed to archive Custom field',
    restoreCustomField: 'Failed to restore Custom field',
    updateTypes: 'Failed to update Types',
  };


  const executeMutation = async ({ actionType, payload }: MutationAction) => {
    const serviceMethod = actionMap[actionType];
    if (!serviceMethod) {
      enqueueSnackbar('Error processing your request', { variant: 'error' });
    } else {
      return serviceMethod(payload);
    }
  };

  const mutation = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType, payload }) => {
      setIsUpdating((prev) => ({ ...prev, [actionType]: true }));


      // Optimistic update for updateTypes action
      if (actionType === 'updateTypes') {
        if (typeof payload.Cid !== 'number') return
        const previousData = queryClient.getQueryData(['custom-field-by-id', payload.Cid]);

        // Update the cache optimistically
        queryClient.setQueryData(['custom-field-by-id', payload.Cid], (old: any) => {
          if (!old) return old;

          // Find and update the TypeList for the specific customer
          const updatedTypeList = old.TypeList.map((type: any) => {
            if (type.CustomerID === payload.CustomerID) {
              return {
                ...type,
                TypeList: payload.data.map((typeId: number) => ({
                  TypeID: typeId,
                  TypeName: '' // TypeName will be updated when query is refetched
                }))
              };
            }
            return type;
          });

          return {
            ...old,
            TypeList: updatedTypeList
          };
        });

        // Return context for rollback
        return { previousData };
      }
    },



    onSuccess: (response: any, { actionType }) => {

      if (response?.data?.Success === false) {
        const failureMessage = failureMessages[actionType] || 'Operation failed';
        enqueueSnackbar(failureMessage, { variant: 'error' });
        return
      }

      const successMessage = successMessages[actionType] || 'Operation successful';
      if (actionType !== 'updateTypes') {
        enqueueSnackbar(successMessage, { variant: 'success' });
        navigate({ to: '/custom-fields' });
      }
    },
    onError: (error: any, { actionType }) => {
      const failureMessage = failureMessages[actionType] || 'Operation failed';
      enqueueSnackbar(failureMessage, { variant: 'error' });
    },
    onSettled: ({ actionType }) => {
      if (actionType !== 'updateTypes') {
        queryClient.invalidateQueries({ queryKey: ['custom-field-by-id', params.customFieldId] });
      }
      queryClient.invalidateQueries({ queryKey: ['customFieldsAdmin'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['ticket-types-by-customer'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['custom-field-values-by-type'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['custom-fields-by-ticket-id'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['custom-field-groups'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['custom-fields'], exact: false });

    },

  });

  return { executeCustomFieldAction: mutation.mutate, isUpdating, setIsUpdating, executeCustomFieldActionMutation: mutation };
};



export default useCustomFieldMutation;

